import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Loader2, Plus, Trash2, Check, X, Star, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { name: '', role: '', quote: '', avatar_url: '', rating: 5, approved: true, sort_order: 0 };

/**
 * TestimonialsAdminPanel — super-admin CRUD for landing-page testimonials.
 * Public landing only renders APPROVED entries, so nothing fake ever ships.
 */
const TestimonialsAdminPanel = ({ language = 'en' }) => {
  const es = language === 'es';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/testimonials`, { withCredentials: true });
      setItems(r.data?.testimonials || []);
    } catch (e) {
      toast.error(es ? 'No se pudieron cargar' : 'Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  }, [es]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const save = async () => {
    if (!form.name.trim() || !form.quote.trim()) {
      toast.error(es ? 'Nombre y cita son obligatorios' : 'Name and quote are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/admin/testimonials/${editingId}`, form, { withCredentials: true });
        toast.success(es ? 'Actualizado' : 'Updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/testimonials`, form, { withCredentials: true });
        toast.success(es ? 'Añadido' : 'Added');
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'Error al guardar' : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({
      name: t.name || '', role: t.role || '', quote: t.quote || '',
      avatar_url: t.avatar_url || '', rating: t.rating || 5,
      approved: !!t.approved, sort_order: t.sort_order || 0,
    });
  };

  const toggleApproved = async (t) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/testimonials/${t.id}`,
        { ...t, approved: !t.approved }, { withCredentials: true });
      load();
    } catch (e) {
      toast.error(es ? 'Error' : 'Failed to update');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/testimonials/${id}`, { withCredentials: true });
      toast.success(es ? 'Eliminado' : 'Deleted');
      if (editingId === id) resetForm();
      load();
    } catch (e) {
      toast.error(es ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  return (
    <Card data-testid="testimonials-admin-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-500" />
          {es ? 'Testimonios' : 'Testimonials'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Form */}
        <div className="grid gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{es ? 'Nombre' : 'Name'}</Label>
              <Input data-testid="testimonial-name-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
            </div>
            <div>
              <Label>{es ? 'Rol / Iglesia' : 'Role / Church'}</Label>
              <Input data-testid="testimonial-role-input" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Lead Pastor, Grace Chapel" />
            </div>
          </div>
          <div>
            <Label>{es ? 'Cita' : 'Quote'}</Label>
            <textarea
              data-testid="testimonial-quote-input"
              value={form.quote}
              onChange={(e) => setForm({ ...form, quote: e.target.value })}
              rows={3}
              maxLength={600}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm"
              placeholder={es ? 'Lumera transformó mi estudio…' : 'Lumera transformed my study…'}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>{es ? 'Avatar URL (opcional)' : 'Avatar URL (optional)'}</Label>
              <Input value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://…" />
            </div>
            <div>
              <Label>{es ? 'Estrellas' : 'Rating'}</Label>
              <Input type="number" min={1} max={5} value={form.rating}
                onChange={(e) => setForm({ ...form, rating: Math.max(1, Math.min(5, parseInt(e.target.value) || 5)) })} />
            </div>
            <div>
              <Label>{es ? 'Orden' : 'Sort order'}</Label>
              <Input type="number" value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.approved}
              onChange={(e) => setForm({ ...form, approved: e.target.checked })} />
            {es ? 'Aprobado (visible en la página de inicio)' : 'Approved (visible on landing page)'}
          </label>
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} data-testid="testimonial-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              {editingId ? (es ? 'Guardar cambios' : 'Save changes') : (es ? 'Añadir testimonio' : 'Add testimonial')}
            </Button>
            {editingId && (
              <Button variant="ghost" onClick={resetForm}>{es ? 'Cancelar' : 'Cancel'}</Button>
            )}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> {es ? 'Cargando…' : 'Loading…'}</div>
        ) : items.length === 0 ? (
          <p className="text-slate-500 italic" data-testid="testimonials-empty">
            {es ? 'Aún no hay testimonios. Añade el primero arriba.' : 'No testimonials yet. Add the first one above.'}
          </p>
        ) : (
          <div className="space-y-2" data-testid="testimonials-list">
            {items.map((t) => (
              <div key={t.id} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{t.name}</span>
                    {t.role && <span className="text-xs text-slate-500">· {t.role}</span>}
                    {t.approved
                      ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{es ? 'Visible' : 'Approved'}</Badge>
                      : <Badge variant="secondary" className="text-[10px]">{es ? 'Oculto' : 'Hidden'}</Badge>}
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300 italic mt-1 line-clamp-2">“{t.quote}”</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" title={t.approved ? 'Hide' : 'Approve'} onClick={() => toggleApproved(t)} data-testid={`testimonial-toggle-${t.id}`}>
                    {t.approved ? <X className="w-4 h-4" /> : <Check className="w-4 h-4 text-emerald-600" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => startEdit(t)} data-testid={`testimonial-edit-${t.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(t.id)} data-testid={`testimonial-delete-${t.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TestimonialsAdminPanel;
